import { Suspense } from "react";
import { motion } from "motion/react";
import PropTypes from "prop-types";
import Carousel from "../Carousel";
import GenericSlideThumb from "./SlideThumb";
import { NewsSlideTab, NewsSlideThumb } from "./NewsSlide";
import { StorySlideTab, StorySlideThumb } from "./StorySlide";
import { cockpitComponent } from "../CockpitLayoutComponents";
import { useSettings } from "../../hooks/SettingsHooks";
import { cmsAssetUrl } from "../../utils/CmsUtils";
import { unwrapAsset } from "../../utils/CockpitUtils";
import GenericSlideTab from "./SlideTab";
import "./style.scss";

const ThumbsPlaceholder = () =>
  Array(3)
    .fill(1)
    .map((_, i) => (
      <motion.div
        key={"placeholder-" + i}
        className="slide-thumb slide-thumb-placeholder"
      >
        <div
          className="slide-thumb-placeholder-bkg"
          style={{ animationDelay: i * 0.3 + "s" }}
        />
        <div className="slide-thumb-placeholder-label">
          <div></div>
        </div>
      </motion.div>
    ));

const TabsPlaceholder = () =>
  Array(3)
    .fill(1)
    .map((_, i) => (
      <motion.div
        key={"placeholder-" + i}
        className="slide-tab slide-tab-placeholder"
      >
        <div className="slide-tab-placeholder-label">
          <div></div>
        </div>
      </motion.div>
    ));

const HeroCarousel = ({
  showThumbs = true,
  autoplayInterval = 0,
  navType = "thumbs",
  fixedWidthSlides = false,
  id = null,
  style = {},
  children: _children
}) => {
  const headingTags = [
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "h6" /* intentional */
  ];
  //Decrease all heading tags inside the carousel (for SEO reasons)
  const children = _children.map(ch => ({
    ...ch,
    children:
      ch.component === "herobox"
        ? ch.children.map(chch => ({
            ...chch,
            settings: Object.assign(
              {}, //make a copy
              chch.settings,
              chch.component === "bjelin_heading" &&
                headingTags.includes(chch.settings.tag)
                ? {
                    tag: headingTags[headingTags.indexOf(chch.settings.tag) + 1]
                  }
                : {}
            )
          }))
        : ch.children
  }));

  const fallbackSlide = children.filter(
    s =>
      s.component !== "carousel_slide_news" &&
      s.component !== "carousel_slide_story"
  )?.[0];

  return (
    <div id={id} className="hero-carousel" style={style}>
      <Suspense fallback={fallbackSlide && cockpitComponent(fallbackSlide)}>
        <Carousel
          loop={navType !== "tabs"}
          autoplayInterval={autoplayInterval}
          navType={navType}
          showMarkers={navType !== "tabs"}
          hideButtons={navType !== "tabs"}
          fixedWidthSlides={fixedWidthSlides}
          slidesData={children}
          renderSlide={(ch, _index) => {
            ch.settings.class = "carousel-slide " + ch.settings.class;
            return cockpitComponent(ch);
          }}
          renderThumb={
            navType === "thumbs" && showThumbs
              ? (ch, index) => <SlideThumb key={index} {...ch} />
              : undefined
          }
          renderTab={
            navType === "tabs"
              ? (ch, index) => <SlideTab key={index} {...ch} />
              : undefined
          }
          renderThumbPlaceholder={ThumbsPlaceholder}
          renderTabPlaceholder={TabsPlaceholder}
        />
      </Suspense>
    </div>
  );
};

HeroCarousel.propTypes = {
  id: PropTypes.string,
  style: PropTypes.object,
  autoplayInterval: PropTypes.number,
  navType: PropTypes.oneOf(["thumbs", "dots", "tabs", "arrows"]),
  showThumbs: PropTypes.bool
};

const SlideThumb = props => {
  return props.component === "carousel_slide_story" ? (
    <StorySlideThumb
      {...props}
      id={props?.settings?.story?._id}
      key={props.index}
    />
  ) : props.component === "carousel_slide_news" ? (
    <NewsSlideThumb {...props} key={props.index} />
  ) : (
    <HeroboxSlideThumb {...props} key={props.index} />
  );
};

const SlideTab = props => {
  return props.component === "carousel_slide_story" ? (
    <StorySlideTab
      {...props}
      id={props?.settings?.story?._id}
      key={props.index}
    />
  ) : props.component === "carousel_slide_news" ? (
    <NewsSlideTab {...props} key={props.index} />
  ) : (
    <HeroboxSlideTab {...props} key={props.index} />
  );
};

const HeroboxSlideThumb = props => {
  const backgroundImage = unwrapAsset(
    props.settings.thumb_image || props.settings.background_image
  );
  const settings = useSettings();
  const bImage = backgroundImage
    ? typeof backgroundImage === "object"
      ? backgroundImage
      : { path: backgroundImage, image: true }
    : {
        ...settings.default_bg_image,
        path: cmsAssetUrl(settings.default_bg_image.path)
      };

  const image = bImage.styles ? bImage.styles.px256 : bImage.path;
  const label = props?.children?.[0]?.settings?.text;

  return (
    <GenericSlideThumb
      image={image}
      label={label}
      isActive={props.isActive}
      onClick={props.onClick}
    />
  );
};

const HeroboxSlideTab = props => {
  const label = props?.children?.[0]?.settings?.text;

  return (
    <GenericSlideTab
      label={label}
      isActive={props.isActive}
      onClick={props.onClick}
    />
  );
};

export default HeroCarousel;
